import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect } from 'react'
import TextareaField from '../../elements/form/TextareaField'
import RadioGroup from '../../elements/form/RadioGroup'
import { createExeat } from '../../../store/actions/exeatsActions'
import { ERROR, GET_STUDENTS } from '../../../store/types'
import ModalActionButton from '../../elements/ModalActionButton'
import DateField from '../../elements/form/DateField'
import moment from 'moment'
import TextField from '../../elements/form/TextField'
import ApplicantTableSnippet from '../applications/ApplicantTableSnippet';
import Loader from '../../elements/Loader';
import SearchField from '../../elements/SearchField';
import CloseIcon from '../../elements/icons/CloseIcon';
import CheckIcon from '../../elements/icons/CheckIcon';
import { debounce } from '../../../utils';
import { fetchStudents } from '../../../store/actions/studentsActions';

const NewExeat = ({student, term, close, reload}) => {
    const dispatch = useDispatch()
    const exeatsSelector = useSelector(state => state.exeats)
    const studentsSelector = useSelector(state => state.students)
    const [exeatPayload, setExeatPayload] = useState({
        student: student ? student._id : '',
        term: term ? term._id : ''
    })

    const [validationErrors, setValidationErrors] = useState({})

    const exeatBases = [
        {
            label: 'Religious',
            value: 'religious'
        },
        {
            label: 'Medical',
            value: 'medical'
        },
        {
            label: 'Holiday',
            value: 'holiday'
        },
        {
            label: 'Other',
            value: 'other'
        },
    ]

    useEffect(() => {
        if(!student) {
            dispatch({
                type: GET_STUDENTS,
                payload: []
            })
        }
        return () => {
            
        };
    }, [dispatch, student]);

    const validateForm = () => {
        let errors = {}

        if(!exeatPayload.student || exeatPayload.student === '') {
            errors.student = true
        }
        
        if(!exeatPayload.reasonType || exeatPayload.reasonType === '') {
            errors.reasonType = true
        }
  
        if(!exeatPayload.dateFrom || exeatPayload.dateFrom === '') {
            errors.dateFrom = true
        }

        if(!exeatPayload.dateTo || exeatPayload.dateTo === '') {
            errors.dateTo = true
        }

        if(!exeatPayload.pickupName || exeatPayload.pickupName === '') {
            errors.pickupName = true
        }

        if(!exeatPayload.pickupEmail || exeatPayload.pickupEmail === '') {
            errors.pickupEmail = true
        }

        if(!exeatPayload.pickupPhone || exeatPayload.pickupPhone === '') {
            errors.pickupPhone = true
        }
        setValidationErrors(errors)
        return errors
    }

    const pushExeat = () => {
        if (Object.values(validateForm()).includes(true)) {
            dispatch({
                type: ERROR,
                error: {response: {data: {
                    message: 'Please check the highlighted fields'
                }}}
            });
            return
        }
        const payload = {
            ...exeatPayload,
            dates: {
                from: exeatPayload.dateFrom,
                to: exeatPayload.dateTo
            },
            pickup: {
                name: exeatPayload.pickupName,
                phone: exeatPayload.pickupPhone,
                email: exeatPayload.pickupEmail
            }
        }
        dispatch(createExeat(payload))
    }

    const searchStudents = debounce((term) => {
        if(!term || term === '') {
            dispatch({
                type: GET_STUDENTS,
                payload: []
            })
            return
        }
        dispatch(fetchStudents(`searchTerm=${term}`, 0, 0))
    }, 300)


    return (
        <div className='w-full'>
            {!student && <>
                <h3 className='text-sm font-[550]'>Find and select a student</h3>
                <p className='text-sm text-gray-500'>Search for a student below to create an exeat for them.</p>

                <div className='w-full mt-[20px] mb-[10px]'>
                    <SearchField 
                        placeholderText={`Search by student name or admission number`}
                        showResultsDropDown={false}
                        triggerSearch={(term)=>{searchStudents(term)}}
                        performingSearch={studentsSelector.loadingStudents}
                    />
                </div>
                {studentsSelector.loadingStudents ? 
                    <Loader />
                :
                <div className='overflow-y-scroll scrollbar-hidden max-h-[550px]'>
                    {studentsSelector?.students?.students?.length > 0 ? <div className='mt-[10px] w-full'>
                        {studentsSelector?.students?.students?.map((student, studentIndex) => (
                            <div key={studentIndex} className='flex items-start justify-between my-[10px]'>
                                <ApplicantTableSnippet 
                                    applicant={student} 
                                    showAdmissionNumber={true} 
                                    imageHeightClass='h-[35px]'
                                    imageWidthClass='w-[35px]'
                                />
                                <button onClick={()=>{setExeatPayload({...exeatPayload, student: student._id})}} className={`
                                    p-[2px] rounded transition duration-200 ${exeatPayload?.student === student._id ? 
                                    'bg-gray-600 text-white hover:text-gray-300 active:bg-black' 
                                    : 
                                    'bg-gray-100 text-gray-400  active:bg-white hover:text-gray-600'}
                                `}>
                                    {exeatPayload?.student === student._id ? 
                                        <CloseIcon className="w-5 h-5" /> 
                                        :
                                        <CheckIcon className="w-5 h-5" />
                                    }
                                </button>
                            </div>
                        ))}
                    </div>
                    :
                    <div className='mt-[10px] p-3 rounded-lg border border-gray-400 bg-gray-50'>
                        <p className="text-xs font-medium">No students found matching your search (or you haven't searched yet). Use the field above to search for a student by name or admission number</p>
                    </div>
                    }
                </div>
                }
            </>}
            <div className='mb-2 pt-4 border-t border-gray-200 mt-4'>
                <RadioGroup 
                    items={exeatBases} 
                    inline={true}
                    preSelectedIndex={exeatBases.findIndex(item => item.value === exeatPayload.reasonType)}
                    inputLabel={'Basis for exeat'}
                    requiredField={true}
                    returnSelected={(value) => {setExeatPayload({...exeatPayload, ...{reasonType: value.value}})}} 
                    hasError={validationErrors && validationErrors.reasonType}  
                    titleField='label'
                />
            </div>

            <div className='my-2 w-full'>
                <TextareaField
                    inputLabel="Exeat note/Reason" 
                    fieldId="check-n-note" 
                    inputType="text" 
                    requiredField={false}
                    inputPlaceholder="You can add a note if necessary"
                    preloadValue={exeatPayload.reason || ''}
                    hasError={false} 
                    returnFieldValue={(value)=>{setExeatPayload({...exeatPayload, reason: value})}}
                />
            </div>

            <div className='w-full flex items-start justify-between gap-x-[10px]'>
                <div className='my-2 w-full'>
                    <DateField
                        inputLabel="From" 
                        fieldId="start-date" 
                        requiredField={true}
                        inputType="text" 
                        preloadValue={(moment(exeatPayload.dateFrom).format('YYYY-MM-DD')) || ''}
                        hasError={validationErrors && validationErrors.dateFrom} 
                        returnFieldValue={(value)=>{
                            // const formattedDate = (moment(value, 'YYYY-MM-DD').format('DD-MM-YYYY'))
                            setExeatPayload({...exeatPayload, ...{dateFrom: new Date(value)}})
                        }}
                    />
                </div>
                <div className='my-2 w-full'>
                    <DateField
                        inputLabel="To" 
                        fieldId="end-date" 
                        requiredField={true}
                        inputType="text" 
                        preloadValue={(moment(exeatPayload.dateTo).format('YYYY-MM-DD')) || ''}
                        hasError={validationErrors && validationErrors.dateTo} 
                        returnFieldValue={(value)=>{
                            // const formattedDate = (moment(value, 'YYYY-MM-DD').format('DD-MM-YYYY'))
                            setExeatPayload({...exeatPayload, ...{dateTo: new Date(value)}})
                        }}
                    />
                </div>
            </div>

            <div className='mb-2 mt-4 w-full'>
                <h3 className='font-space-grotesk text-[15px] font-[550] mb-[5px]'>Student Pickup</h3>
                <p className='text-sm text-gray-600'>Please provide details of the person that will pick up the student from the college. This individual will receive a pickup code to present to school security when the exeat is approved.</p>
            </div>

            <div className='my-2 w-full'>
                <TextField
                    inputLabel="Name" 
                    fieldId="pickup-name" 
                    inputType="text" 
                    inputPlaceholder={`Full name of the person`}
                    preloadValue={''}
                    hasError={validationErrors.pickupName} 
                    returnFieldValue={(value)=>{setExeatPayload({...exeatPayload, ...{pickupName: value}})}}
                    requiredField={true}
                />
            </div>

            <div className='my-2 w-full'>
                <TextField
                    inputLabel="Email address" 
                    fieldId="pickup-email" 
                    inputType="text"
                    preloadValue={``} 
                    inputPlaceholder={'An active email address'}
                    hasError={validationErrors.pickupEmail} 
                    returnFieldValue={(value)=>{setExeatPayload({...exeatPayload, ...{pickupEmail: value}})}}
                    requiredField={true}
                />
            </div>

            <div className='my-2 w-full'>
                <TextField
                    inputLabel="Phone number" 
                    fieldId="pickup-phone" 
                    inputType="text"
                    preloadValue={''} 
                    inputPlaceholder={`The person's active phone number`}
                    hasError={validationErrors.pickupPhone} 
                    returnFieldValue={(value)=>{setExeatPayload({...exeatPayload, ...{pickupPhone: value}})}}
                    requiredField={true}
                />
            </div>
            <div className="mt-8 flex flex-row-reverse gap-x-4">
                <ModalActionButton
                    buttonLabel={`Create Exeat`}
                    buttonAction={()=>{pushExeat()}}
                    processing={exeatsSelector.creatingExeat}
                />

                <button
                    type="button"
                    className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-xl hover:bg-opacity-20 transition duration-200 hover:bg-secondary focus:outline-none"
                    onClick={()=>{close()}}
                >
                    Close
                </button>
                
            </div>
        </div>
    )
}

export default NewExeat